import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  RefreshCw,
  Calendar,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Download,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  FileSearch,
  ShieldAlert,
  CheckCircle2,
  ScanLine
} from 'lucide-react'
import { AdminService } from '../../services/adminService'

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  FORENSIC DOSSIER — Dispatch Investigation View                           ║
// ║  Self-contained sub-component for the "Dispatch Investigation" tab.       ║
// ║  Aesthetic: case-file/lab-notebook with serif display, mono evidence,     ║
// ║  warm parchment background, sangria + emerald + alert-red accents.        ║
// ╚══════════════════════════════════════════════════════════════════════════╝

const DOSSIER_FONT_VARS = {
  '--ff-display': '"Fraunces", "Times New Roman", Georgia, serif',
  '--ff-body': '"Manrope", system-ui, -apple-system, sans-serif',
  '--ff-mono': '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  '--ink-primary': '#1A1410',
  '--ink-secondary': '#5C544A',
  '--paper': '#FBF7F2',
  '--paper-soft': '#F4EDE2',
  '--paper-line': '#E5DBC8',
  '--accent-sangria': '#A73A5D',
  '--accent-alert': '#B91C1C',
  '--accent-emerald': '#047857',
}

const FilterPill = ({ active, onClick, children, tone = 'default' }) => {
  const tones = {
    default: active
      ? 'bg-[#1A1410] text-[#FBF7F2] ring-[#1A1410]'
      : 'bg-transparent text-[#5C544A] ring-[#E5DBC8] hover:ring-[#1A1410]/40 hover:text-[#1A1410]',
    alert: active
      ? 'bg-[#B91C1C] text-[#FBF7F2] ring-[#B91C1C]'
      : 'bg-transparent text-[#B91C1C] ring-[#B91C1C]/40 hover:ring-[#B91C1C] hover:bg-[#B91C1C]/5',
    emerald: active
      ? 'bg-[#047857] text-[#FBF7F2] ring-[#047857]'
      : 'bg-transparent text-[#047857] ring-[#047857]/40 hover:ring-[#047857] hover:bg-[#047857]/5',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ fontFamily: 'var(--ff-body)' }}
      className={`px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] rounded-full ring-1 ring-inset transition-all duration-200 ${tones[tone]}`}
    >
      {children}
    </button>
  )
}

const StatusBadge = ({ entry }) => {
  if (!entry.resolved_at) {
    return (
      <span
        style={{ fontFamily: 'var(--ff-mono)' }}
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#B91C1C]"
      >
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#B91C1C] opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#B91C1C]" />
        </span>
        UNRESOLVED
      </span>
    )
  }
  const labels = {
    backfilled: 'BACKFILLED',
    manual_fix: 'MANUAL FIX',
    false_positive: 'FALSE POSITIVE',
    ignored: 'IGNORED',
  }
  return (
    <span
      style={{ fontFamily: 'var(--ff-mono)' }}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#047857]"
    >
      <CheckCircle2 className="h-3 w-3" />
      {labels[entry.resolution] || 'RESOLVED'}
    </span>
  )
}

const KindLabel = ({ kind }) => {
  const map = {
    ghost_dispatched: { text: 'GHOST_DISPATCHED', tone: 'text-[#A73A5D]' },
    ready_unqueued: { text: 'READY_UNQUEUED', tone: 'text-[#9A6B0F]' },
  }
  const v = map[kind] || { text: kind?.toUpperCase() || 'UNKNOWN', tone: 'text-[#5C544A]' }
  return (
    <span
      style={{ fontFamily: 'var(--ff-mono)' }}
      className={`text-[10px] font-semibold tracking-[0.18em] ${v.tone}`}
    >
      {v.text}
    </span>
  )
}

// One-line evidence row in mono key/value form
const EvidenceRow = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 border-b border-dashed border-[#E5DBC8] last:border-0">
    <div
      style={{ fontFamily: 'var(--ff-mono)' }}
      className="text-[10px] font-medium uppercase tracking-[0.16em] text-[#8A7F70] pt-0.5"
    >
      {label}
    </div>
    <div
      style={{ fontFamily: mono ? 'var(--ff-mono)' : 'var(--ff-body)' }}
      className={`text-[12px] ${mono ? 'text-[#1A1410]' : 'text-[#1A1410] font-medium'}`}
    >
      {value || <span className="text-[#B91C1C] italic">unresolved</span>}
    </div>
  </div>
)

const DispatchInvestigationView = () => {
  const [entries, setEntries] = useState([])
  const [stats, setStats] = useState({ ghost_dispatched: 0, ready_unqueued: 0, total: 0 })
  const [statusFilter, setStatusFilter] = useState('unresolved')
  const [kindFilter, setKindFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [resolveModal, setResolveModal] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const loadEntries = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [logResult, statsResult] = await Promise.all([
        AdminService.getGhostDispatchLog({
          status: statusFilter,
          kind: kindFilter === 'all' ? null : kindFilter,
          limit: 100,
        }),
        AdminService.getGhostDispatchLogStats(),
      ])
      if (logResult.success) {
        setEntries(logResult.entries || [])
      } else {
        setError(logResult.error || 'Failed to load dispatch log')
      }
      if (statsResult.success) {
        setStats(statsResult.stats || { ghost_dispatched: 0, ready_unqueued: 0, total: 0 })
      }
    } catch (e) {
      setError(e?.message || 'Unexpected error')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, kindFilter])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  const runScanNow = async () => {
    setScanning(true)
    try {
      await AdminService.triggerGhostDetection()
      await loadEntries()
    } finally {
      setScanning(false)
    }
  }

  const handleBackfill = async (entry) => {
    if (!entry.consultation_id || !entry.expected_pharmacist_username) return
    setBusyId(entry.log_id)
    try {
      await AdminService.backfillGhostAssignedPharmacist({
        consultationId: entry.consultation_id,
        expectedUsername: entry.expected_pharmacist_username,
        logId: entry.log_id,
        notes: 'Backfilled from investigation tab',
      })
      await loadEntries()
    } finally {
      setBusyId(null)
    }
  }

  const formatRelative = (iso) => {
    if (!iso) return '—'
    const ms = Date.now() - new Date(iso).getTime()
    const minutes = Math.round(ms / 60000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.round(hours / 24)
    return `${days}d ago`
  }

  const formatExact = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    })
  }

  return (
    <div
      style={{ ...DOSSIER_FONT_VARS, backgroundColor: 'var(--paper)' }}
      className="relative min-h-[60vh]"
    >
      {/* Subtle decorative grid in the background — like graph paper */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, var(--ink-primary) 1px, transparent 1px), linear-gradient(to bottom, var(--ink-primary) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Editorial header */}
        <div className="mb-10 pb-8 border-b-2 border-[#1A1410]">
          <div className="flex items-baseline gap-4 mb-3">
            <span
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#A73A5D]"
            >
              Forensic Dossier №01
            </span>
            <span className="h-px flex-1 bg-[#1A1410]/20" />
            <span
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="text-[10px] uppercase tracking-[0.2em] text-[#8A7F70]"
            >
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
          </div>
          <h2
            style={{
              fontFamily: 'var(--ff-display)',
              fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 0, "WONK" 0',
              letterSpacing: '-0.02em',
            }}
            className="text-5xl md:text-6xl text-[#1A1410] leading-[1.05]"
          >
            Dispatch
            <span style={{ fontVariationSettings: '"opsz" 144, "wght" 400, "SOFT" 100, "WONK" 1' }} className="italic"> Investigation</span>
            <span className="text-[#A73A5D]">.</span>
          </h2>
          <p
            style={{ fontFamily: 'var(--ff-body)' }}
            className="mt-3 text-[15px] text-[#5C544A] max-w-2xl leading-relaxed"
          >
            Persistent log of every consultation that was dispatched but never received a V5 assignment write,
            or reached a "ready" state without ever entering the pending queue. Auto-detected every 5 minutes.
          </p>

          {/* Stats strip */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[#1A1410]/15">
            <div className="px-0 md:pr-8 py-5 border-b md:border-b-0 md:border-r border-[#1A1410]/15">
              <div
                style={{ fontFamily: 'var(--ff-mono)' }}
                className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A7F70] mb-2"
              >
                Active Investigations
              </div>
              <div className="flex items-baseline gap-2">
                <span
                  style={{
                    fontFamily: 'var(--ff-display)',
                    fontVariationSettings: '"opsz" 144, "wght" 700',
                    letterSpacing: '-0.04em',
                  }}
                  className={`text-6xl ${stats.total > 0 ? 'text-[#B91C1C]' : 'text-[#1A1410]'}`}
                >
                  {String(stats.total).padStart(2, '0')}
                </span>
                <span style={{ fontFamily: 'var(--ff-body)' }} className="text-sm text-[#5C544A]">
                  open cases
                </span>
              </div>
            </div>
            <div className="px-0 md:px-8 py-5 border-b md:border-b-0 md:border-r border-[#1A1410]/15">
              <div
                style={{ fontFamily: 'var(--ff-mono)' }}
                className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A73A5D] mb-2"
              >
                Ghost Dispatched
              </div>
              <div
                style={{
                  fontFamily: 'var(--ff-display)',
                  fontVariationSettings: '"opsz" 144, "wght" 600',
                  letterSpacing: '-0.03em',
                }}
                className="text-4xl text-[#1A1410]"
              >
                {String(stats.ghost_dispatched).padStart(2, '0')}
              </div>
              <div style={{ fontFamily: 'var(--ff-body)' }} className="text-xs text-[#5C544A] mt-1">
                Dispatched · V5 never wrote pharmacist
              </div>
            </div>
            <div className="px-0 md:pl-8 py-5">
              <div
                style={{ fontFamily: 'var(--ff-mono)' }}
                className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9A6B0F] mb-2"
              >
                Ready Unqueued
              </div>
              <div
                style={{
                  fontFamily: 'var(--ff-display)',
                  fontVariationSettings: '"opsz" 144, "wght" 600',
                  letterSpacing: '-0.03em',
                }}
                className="text-4xl text-[#1A1410]"
              >
                {String(stats.ready_unqueued).padStart(2, '0')}
              </div>
              <div style={{ fontFamily: 'var(--ff-body)' }} className="text-xs text-[#5C544A] mt-1">
                Ready · never queued · no assignment
              </div>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-2">
            <span
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A7F70] mr-1"
            >
              Status
            </span>
            <FilterPill active={statusFilter === 'unresolved'} onClick={() => setStatusFilter('unresolved')} tone="alert">
              Unresolved
            </FilterPill>
            <FilterPill active={statusFilter === 'resolved'} onClick={() => setStatusFilter('resolved')} tone="emerald">
              Resolved
            </FilterPill>
            <FilterPill active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
              All
            </FilterPill>
            <span className="mx-2 h-4 w-px bg-[#1A1410]/20" />
            <span
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A7F70] mr-1"
            >
              Kind
            </span>
            <FilterPill active={kindFilter === 'all'} onClick={() => setKindFilter('all')}>
              All
            </FilterPill>
            <FilterPill active={kindFilter === 'ghost_dispatched'} onClick={() => setKindFilter('ghost_dispatched')}>
              Ghost Dispatched
            </FilterPill>
            <FilterPill active={kindFilter === 'ready_unqueued'} onClick={() => setKindFilter('ready_unqueued')}>
              Ready Unqueued
            </FilterPill>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={runScanNow}
              disabled={scanning}
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#1A1410] bg-transparent border border-[#1A1410] hover:bg-[#1A1410] hover:text-[#FBF7F2] transition-all duration-200 disabled:opacity-50"
            >
              <ScanLine className={`h-3.5 w-3.5 ${scanning ? 'animate-pulse' : ''}`} />
              {scanning ? 'Scanning…' : 'Scan now'}
            </button>
            <button
              type="button"
              onClick={loadEntries}
              disabled={loading}
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5C544A] bg-transparent border border-[#E5DBC8] hover:border-[#1A1410] hover:text-[#1A1410] transition-all duration-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{ fontFamily: 'var(--ff-body)' }}
            className="mb-6 p-4 bg-[#FEF2F2] border-l-4 border-[#B91C1C] text-[#7F1D1D] text-sm flex items-start gap-2"
          >
            <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {/* Empty state */}
        {!loading && entries.length === 0 && (
          <div className="py-20 text-center border border-dashed border-[#E5DBC8] rounded-sm">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full border-2 border-[#047857] mb-5">
              <CheckCircle2 className="h-7 w-7 text-[#047857]" />
            </div>
            <h3
              style={{
                fontFamily: 'var(--ff-display)',
                fontVariationSettings: '"opsz" 144, "wght" 500, "SOFT" 50, "WONK" 0',
              }}
              className="text-3xl text-[#1A1410] italic"
            >
              No active investigations
            </h3>
            <p
              style={{ fontFamily: 'var(--ff-body)' }}
              className="mt-2 text-sm text-[#5C544A] max-w-md mx-auto"
            >
              {statusFilter === 'unresolved'
                ? "All clear. Either the dispatcher is healthy or every known regression has been resolved."
                : "No entries match the current filters."}
            </p>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && entries.length === 0 && (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-40 bg-[#F4EDE2] animate-pulse rounded-sm border-l-2 border-[#E5DBC8]"
                style={{ animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
        )}

        {/* Case files */}
        <div className="space-y-5">
          {entries.map((entry, idx) => {
            const caseNum = String(idx + 1).padStart(3, '0')
            const accent = entry.resolved_at ? '#047857' : entry.kind === 'ready_unqueued' ? '#9A6B0F' : '#B91C1C'
            const isBusy = busyId === entry.log_id
            return (
              <article
                key={entry.log_id}
                className="relative bg-white border border-[#E5DBC8] shadow-[0_1px_0_rgba(26,20,16,0.04),0_4px_24px_-12px_rgba(26,20,16,0.12)] hover:shadow-[0_1px_0_rgba(26,20,16,0.08),0_8px_32px_-12px_rgba(26,20,16,0.18)] transition-shadow duration-300"
                style={{ borderLeft: `4px solid ${accent}` }}
              >
                {/* Card head — case number + status */}
                <header className="flex items-center justify-between px-6 py-4 border-b border-[#E5DBC8]">
                  <div className="flex items-baseline gap-4">
                    <span
                      style={{
                        fontFamily: 'var(--ff-display)',
                        fontVariationSettings: '"opsz" 14, "wght" 400, "SOFT" 100, "WONK" 1',
                      }}
                      className="text-2xl italic text-[#A73A5D]"
                    >
                      Case
                    </span>
                    <span
                      style={{ fontFamily: 'var(--ff-mono)' }}
                      className="text-2xl font-semibold tracking-[0.05em] text-[#1A1410]"
                    >
                      {caseNum}
                    </span>
                    <span className="hidden md:inline-block h-4 w-px bg-[#E5DBC8]" />
                    <KindLabel kind={entry.kind} />
                  </div>
                  <StatusBadge entry={entry} />
                </header>

                {/* Card body — subject + evidence */}
                <div className="px-6 py-5 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
                  {/* Subject column */}
                  <div className="lg:border-r lg:border-dashed lg:border-[#E5DBC8] lg:pr-6">
                    <div
                      style={{ fontFamily: 'var(--ff-mono)' }}
                      className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#8A7F70] mb-2"
                    >
                      Subject
                    </div>
                    <h3
                      style={{
                        fontFamily: 'var(--ff-display)',
                        fontVariationSettings: '"opsz" 36, "wght" 600',
                      }}
                      className="text-2xl text-[#1A1410] leading-tight"
                    >
                      {entry.user_name || (
                        <span className="italic text-[#5C544A]">Unnamed</span>
                      )}
                    </h3>
                    {entry.user_phone && (
                      <div
                        style={{ fontFamily: 'var(--ff-mono)' }}
                        className="text-xs text-[#5C544A] mt-1"
                      >
                        {entry.user_phone}
                      </div>
                    )}
                    <div
                      style={{ fontFamily: 'var(--ff-mono)' }}
                      className="text-[10px] text-[#8A7F70] mt-3 break-all"
                    >
                      user_id · {entry.user_id?.slice(0, 8)}…
                      <br />
                      {entry.pending_id && <>pending · {entry.pending_id.slice(0, 8)}…</>}
                    </div>

                    {entry.consultation_type && (
                      <div className="mt-4">
                        <span
                          style={{ fontFamily: 'var(--ff-mono)' }}
                          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.16em] text-[#1A1410] border border-[#1A1410] px-2 py-1"
                        >
                          {entry.consultation_type}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Evidence column */}
                  <div>
                    <div
                      style={{ fontFamily: 'var(--ff-mono)' }}
                      className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#8A7F70] mb-3"
                    >
                      <FileSearch className="h-3 w-3" />
                      Evidence
                    </div>
                    <EvidenceRow
                      label="Expected"
                      value={entry.expected_pharmacist_username}
                    />
                    <EvidenceRow
                      label="Dispatcher"
                      value={(entry.dispatcher_source || 'unknown').replace(/_/g, ' ').toUpperCase()}
                      mono
                    />
                    <EvidenceRow
                      label="n8n exec"
                      value={entry.n8n_execution_id || '—'}
                      mono
                    />
                    {entry.dispatched_at && (
                      <EvidenceRow
                        label="Dispatched"
                        value={`${formatRelative(entry.dispatched_at)} · ${formatExact(entry.dispatched_at)}`}
                        mono
                      />
                    )}
                    <EvidenceRow
                      label="Detected"
                      value={`${formatRelative(entry.first_detected_at)} · ${formatExact(entry.first_detected_at)}`}
                      mono
                    />
                    {entry.detection_count > 1 && (
                      <EvidenceRow
                        label="Sightings"
                        value={
                          <span className="text-[#B91C1C] font-semibold">
                            {entry.detection_count}× still unresolved
                          </span>
                        }
                      />
                    )}
                    {entry.consultation_status && (
                      <EvidenceRow
                        label="Status"
                        value={entry.consultation_status}
                        mono
                      />
                    )}
                  </div>
                </div>

                {/* Resolution footer (resolved entries) or actions (unresolved) */}
                {entry.resolved_at ? (
                  <footer
                    style={{ fontFamily: 'var(--ff-body)' }}
                    className="px-6 py-3 bg-[#F4EDE2]/40 border-t border-[#E5DBC8] text-xs text-[#5C544A] flex flex-wrap items-center gap-x-4 gap-y-1"
                  >
                    <span style={{ fontFamily: 'var(--ff-mono)' }} className="text-[10px] uppercase tracking-[0.16em] text-[#047857] font-semibold">
                      Closed
                    </span>
                    <span>
                      by <strong className="text-[#1A1410]">{entry.resolved_by_username || 'unknown'}</strong>
                    </span>
                    <span>·</span>
                    <span>{formatExact(entry.resolved_at)}</span>
                    {entry.resolution_notes && (
                      <>
                        <span>·</span>
                        <span className="italic">"{entry.resolution_notes}"</span>
                      </>
                    )}
                  </footer>
                ) : (
                  <footer className="px-6 py-3 bg-[#F4EDE2]/40 border-t border-[#E5DBC8] flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setResolveModal(entry)}
                      style={{ fontFamily: 'var(--ff-mono)' }}
                      className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5C544A] bg-transparent border border-[#E5DBC8] hover:border-[#1A1410] hover:text-[#1A1410] transition-all"
                    >
                      Mark resolved
                    </button>
                    {entry.expected_pharmacist_username && entry.consultation_id && (
                      <button
                        type="button"
                        onClick={() => handleBackfill(entry)}
                        disabled={isBusy}
                        style={{ fontFamily: 'var(--ff-mono)' }}
                        className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FBF7F2] bg-[#1A1410] hover:bg-[#A73A5D] transition-all duration-200 disabled:opacity-60"
                      >
                        {isBusy ? 'Backfilling…' : 'Backfill assignment'}
                      </button>
                    )}
                  </footer>
                )}
              </article>
            )
          })}
        </div>

        {/* Method note — explains the investigation methodology */}
        <aside
          style={{ fontFamily: 'var(--ff-body)' }}
          className="mt-12 p-6 border-l-4 border-[#A73A5D] bg-[#F4EDE2]/40 max-w-3xl"
        >
          <div
            style={{ fontFamily: 'var(--ff-mono)' }}
            className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A73A5D] mb-3"
          >
            Method · How this works
          </div>
          <p className="text-sm text-[#1A1410] leading-relaxed">
            The <span style={{ fontFamily: 'var(--ff-mono)' }} className="text-[12px] bg-[#FBF7F2] px-1.5 py-0.5 border border-[#E5DBC8]">detect_and_log_ghost_dispatches()</span> Postgres function runs every 5 minutes via pg_cron. It scans for two regression types:{' '}
            <strong>Ghost Dispatched</strong> (queue says <code className="text-[12px]">status='dispatched'</code> but{' '}
            <code className="text-[12px]">user_consultations.assigned_pharmacist</code> is NULL) and{' '}
            <strong>Ready Unqueued</strong> (consultation reached <code className="text-[12px]">'ready'</code> but never appeared in the pending queue).
            Each detection upserts a log row keyed by{' '}
            <code className="text-[12px]">pending_id</code> — repeated detections increment the sightings counter without duplicating cases.
          </p>
          <p className="text-sm text-[#5C544A] mt-3 leading-relaxed">
            <strong className="text-[#1A1410]">Backfill</strong> writes the expected pharmacist username from{' '}
            <code className="text-[12px]">round_robin → admin_users</code> into{' '}
            <code className="text-[12px]">user_consultations.assigned_pharmacist</code> and auto-resolves the case. It does <em>not</em> replay the Respond.io conversation — that may need manual reassignment.
          </p>
        </aside>
      </div>

      {/* Resolve modal */}
      {resolveModal && (
        <ResolveModal
          entry={resolveModal}
          onClose={() => setResolveModal(null)}
          onResolved={() => {
            setResolveModal(null)
            loadEntries()
          }}
        />
      )}
    </div>
  )
}

const ResolveModal = ({ entry, onClose, onResolved }) => {
  const [resolution, setResolution] = useState('manual_fix')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setSubmitting(true)
    setError('')
    try {
      const result = await AdminService.markGhostResolved({
        logId: entry.log_id,
        resolution,
        notes: notes.trim() || null,
      })
      if (result.success) onResolved()
      else setError(result.error || 'Failed to mark as resolved')
    } catch (e) {
      setError(e?.message || 'Unexpected error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      style={DOSSIER_FONT_VARS}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1A1410]/60 px-4 backdrop-blur-sm"
    >
      <div
        style={{ backgroundColor: 'var(--paper)' }}
        className="w-full max-w-lg border border-[#1A1410]/30 shadow-2xl"
      >
        <div className="px-6 pt-6 pb-4 border-b border-[#E5DBC8]">
          <div
            style={{ fontFamily: 'var(--ff-mono)' }}
            className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A73A5D] mb-2"
          >
            Close investigation
          </div>
          <h3
            style={{
              fontFamily: 'var(--ff-display)',
              fontVariationSettings: '"opsz" 36, "wght" 600',
            }}
            className="text-2xl text-[#1A1410]"
          >
            How was this resolved?
          </h3>
          <div
            style={{ fontFamily: 'var(--ff-mono)' }}
            className="text-[11px] text-[#5C544A] mt-1"
          >
            {entry.user_name || entry.user_id?.slice(0, 8)} · {entry.kind}
          </div>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div>
            <div
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A7F70] mb-3"
            >
              Resolution
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { value: 'manual_fix', label: 'Manual fix', hint: 'Fixed via SQL or n8n directly' },
                { value: 'backfilled', label: 'Backfilled', hint: 'Username copied from round_robin' },
                { value: 'false_positive', label: 'False positive', hint: 'Was not actually a ghost' },
                { value: 'ignored', label: 'Ignored', hint: 'Acknowledged, no action' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setResolution(opt.value)}
                  style={{ fontFamily: 'var(--ff-body)' }}
                  className={`text-left px-4 py-3 border transition-all ${
                    resolution === opt.value
                      ? 'border-[#1A1410] bg-white shadow-[2px_2px_0_#1A1410]'
                      : 'border-[#E5DBC8] hover:border-[#1A1410]/40'
                  }`}
                >
                  <div className="text-sm font-semibold text-[#1A1410]">{opt.label}</div>
                  <div className="text-[11px] text-[#5C544A] mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div
              style={{ fontFamily: 'var(--ff-mono)' }}
              className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A7F70] mb-2"
            >
              Notes <span className="font-normal lowercase tracking-normal">(optional)</span>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What did you find? What did you do?"
              style={{ fontFamily: 'var(--ff-body)' }}
              className="w-full px-3 py-2 bg-white border border-[#E5DBC8] text-sm text-[#1A1410] focus:border-[#1A1410] focus:outline-none transition-colors"
            />
          </div>
          {error && (
            <div
              style={{ fontFamily: 'var(--ff-body)' }}
              className="px-3 py-2 bg-[#FEF2F2] border-l-4 border-[#B91C1C] text-xs text-[#7F1D1D]"
            >
              {error}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E5DBC8] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{ fontFamily: 'var(--ff-mono)' }}
            className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5C544A] hover:text-[#1A1410] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            style={{ fontFamily: 'var(--ff-mono)' }}
            className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FBF7F2] bg-[#1A1410] hover:bg-[#A73A5D] transition-colors disabled:opacity-60"
          >
            {submitting ? 'Closing…' : 'Close case'}
          </button>
        </div>
      </div>
    </div>
  )
}

const DailyConsultationReports = () => {
  const navigate = useNavigate()

  // Tab state — 'reports' (existing daily reports) or 'dispatch' (forensic log)
  const [activeTab, setActiveTab] = useState('reports')
  // Lightweight badge counter for the dispatch tab — fetched on mount and tab switch
  const [dispatchUnresolvedCount, setDispatchUnresolvedCount] = useState(0)

  // Date range state - default: Last 30 days
  const today = new Date().toISOString().split('T')[0]
  const last30 = new Date()
  last30.setDate(last30.getDate() - 29)
  const defaultStartDate = last30.toISOString().split('T')[0]

  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(today)

  // Check if user is super_admin (only role allowed to see the dispatch tab)
  const currentUser = AdminService.getCurrentUser()
  const isSuperAdmin = currentUser?.role === 'super_admin'

  // Poll unresolved count to drive the tab badge — only for super_admin
  useEffect(() => {
    if (!isSuperAdmin) return undefined
    let cancelled = false
    const fetchCount = async () => {
      const res = await AdminService.getGhostDispatchLogStats()
      if (!cancelled && res.success) setDispatchUnresolvedCount(res.stats?.total || 0)
    }
    fetchCount()
    const intervalId = setInterval(fetchCount, 60000)
    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [isSuperAdmin])

  // Data state
  const [reportData, setReportData] = useState([])
  const [rankingAudit, setRankingAudit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rankingError, setRankingError] = useState('')

  // Expanded rows state (for daily breakdown)
  const [expandedDates, setExpandedDates] = useState({})
  const [expandedRankingRows, setExpandedRankingRows] = useState({})

  // Load report data
  const loadReportData = useCallback(async () => {
    setLoading(true)
    setError('')
    setRankingError('')

    try {
      const [reportResult, rankingResult] = await Promise.all([
        AdminService.getDailyConsultationReports(startDate, endDate),
        AdminService.getDailyPharmacistRankingAudit(endDate)
      ])

      if (reportResult.success) {
        setReportData(reportResult.data || [])
      } else {
        setError(reportResult.error || 'Failed to load report data')
      }

      if (rankingResult.success) {
        setRankingAudit(rankingResult.data || null)
      } else {
        setRankingAudit(null)
        setRankingError(rankingResult.error || 'Failed to load ranking audit')
      }
    } catch (err) {
      setError('An error occurred while loading reports')
      setRankingAudit(null)
      setRankingError('An error occurred while loading ranking audit')
      console.error('Load reports error:', err)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  // Load data on mount and when date range changes
  useEffect(() => {
    loadReportData()
  }, [loadReportData])

  // Toggle row expansion
  const toggleExpanded = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }))
  }

  const toggleRankingExpanded = (pharmacistName) => {
    setExpandedRankingRows(prev => ({
      ...prev,
      [pharmacistName]: !prev[pharmacistName]
    }))
  }

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatDateTime = (value) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  }

  const formatLabel = (value) => {
    if (!value) return '-'
    return String(value)
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  const getTimingBadge = (timingBucket) => {
    if (timingBucket === 'assigned_and_completed_today') {
      return {
        label: 'Assigned + Completed Today',
        className: 'bg-emerald-50 text-emerald-700'
      }
    }

    if (timingBucket === 'completed_today_from_backlog') {
      return {
        label: 'Completed Today From Earlier Assignment',
        className: 'bg-amber-50 text-amber-700'
      }
    }

    return {
      label: 'Assigned Today Only',
      className: 'bg-sky-50 text-sky-700'
    }
  }

  const formatCountsSummary = (counts = {}) => {
    const items = Object.entries(counts || {})
      .filter(([_, count]) => Number(count || 0) > 0)
      .sort((a, b) => a[0].localeCompare(b[0]))

    if (items.length === 0) return 'none'
    return items.map(([key, count]) => `${key}:${count}`).join(' | ')
  }

  // Calculate grand totals
  const grandTotals = reportData.reduce((acc, day) => ({
    round_robin_assigned: acc.round_robin_assigned + (day.totals.round_robin_assigned || 0),
    assigned_processed: acc.assigned_processed + day.totals.assigned_processed,
    wondura_handled: acc.wondura_handled + day.totals.wondura_handled,
    pending: acc.pending + day.totals.pending,
    paid: acc.paid + day.totals.paid,
  }), { round_robin_assigned: 0, assigned_processed: 0, wondura_handled: 0, pending: 0, paid: 0 })

  // Export to CSV
  const handleExport = () => {
    const headers = [
      'Date',
      'Pharmacist',
      'Round Robin Assigned',
      'Assigned & Processed',
      'Wondura Handled',
      'Pending',
      'Paid',
      'Status Counts',
      'Outcome Counts'
    ]

    const rows = []
    reportData.forEach(day => {
      // Add totals row for each day
      rows.push([
        day.date,
        'TOTAL',
        day.totals.round_robin_assigned || 0,
        day.totals.assigned_processed,
        day.totals.wondura_handled,
        day.totals.pending,
        day.totals.paid,
        `"${formatCountsSummary(day.totals.status_counts)}"`,
        `"${formatCountsSummary(day.totals.outcome_counts)}"`
      ])
      // Add individual pharmacist rows
      Object.entries(day.pharmacists).forEach(([username, stats]) => {
        if (
          stats.round_robin_assigned > 0 ||
          stats.assigned_processed > 0 ||
          stats.wondura_handled > 0 ||
          stats.pending > 0 ||
          stats.paid > 0
        ) {
          rows.push([
            day.date,
            username,
            stats.round_robin_assigned || 0,
            stats.assigned_processed,
            stats.wondura_handled,
            stats.pending,
            stats.paid,
            `"${formatCountsSummary(stats.status_counts)}"`,
            `"${formatCountsSummary(stats.outcome_counts)}"`
          ])
        }
      })
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `daily_reports_${startDate}_to_${endDate}.csv`
    link.click()
  }

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Calculate pagination
  const totalPages = Math.ceil(reportData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedData = reportData.slice(startIndex, endIndex)

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1)
  }, [reportData.length])

  useEffect(() => {
    setExpandedRankingRows({})
  }, [rankingAudit?.report_date])

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <BarChart3 className="w-6 h-6 text-purple-600 mr-2" />
                <h1 className="text-xl font-semibold text-gray-900">Daily Consultation Reports</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExport}
                disabled={loading || reportData.length === 0}
                className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </button>
              <button
                onClick={loadReportData}
                disabled={loading}
                className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      {isSuperAdmin && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-6" aria-label="Reports tabs">
              <button
                type="button"
                onClick={() => setActiveTab('reports')}
                className={`relative py-3 text-sm font-medium transition-colors ${
                  activeTab === 'reports'
                    ? 'text-purple-600 border-b-2 border-purple-600 -mb-px'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                Daily Reports
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('dispatch')}
                className={`relative py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === 'dispatch'
                    ? 'text-[#A73A5D] border-b-2 border-[#A73A5D] -mb-px'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                Dispatch Investigation
                {dispatchUnresolvedCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#B91C1C] text-white text-[10px] font-bold">
                    {dispatchUnresolvedCount}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Dispatch Investigation tab — separate aesthetic */}
      {activeTab === 'dispatch' && isSuperAdmin && <DispatchInvestigationView />}

      {/* Daily Reports tab content (default) */}
      {activeTab === 'reports' && (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Range Filter */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-gray-500 mr-2" />
              <span className="text-sm font-medium text-gray-700">Date Range:</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={today}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            {/* Quick date range buttons */}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => {
                  setStartDate(today)
                  setEndDate(today)
                }}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => {
                  const last7 = new Date()
                  last7.setDate(last7.getDate() - 6)
                  setStartDate(last7.toISOString().split('T')[0])
                  setEndDate(today)
                }}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Last 7 Days
              </button>
              <button
                onClick={() => {
                  const last30 = new Date()
                  last30.setDate(last30.getDate() - 29)
                  setStartDate(last30.toISOString().split('T')[0])
                  setEndDate(today)
                }}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Last 30 Days
              </button>
              <button
                onClick={() => {
                  setStartDate('2025-01-01')
                  setEndDate(today)
                }}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                All Time
              </button>
            </div>
          </div>
        </div>

        {rankingError && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0" />
            <p className="text-amber-700">{rankingError}</p>
          </div>
        )}

        {!loading && rankingAudit && (
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6 border border-slate-200">
            <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Sent Daily Ranking Audit</h2>
                  <p className="text-sm text-slate-600 mt-1">
                    This section matches the sent email logic for business date <span className="font-semibold">{rankingAudit.report_date}</span>.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Window: {formatDateTime(rankingAudit.window_start_utc)} to {formatDateTime(rankingAudit.window_end_utc)} ({rankingAudit.timezone_name})
                  </p>
                </div>
                <div className="text-xs text-slate-500">
                  Uses canonical <span className="font-semibold">user_consultations.completed_at</span> with assignment-log attribution fallback only when needed.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-slate-50/60 border-b border-slate-200">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-sm font-medium text-slate-600">Included Consultations</p>
                <p className="text-2xl font-semibold text-slate-900">{rankingAudit.total_consultations || 0}</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-white p-4">
                <p className="text-sm font-medium text-emerald-700">Attributed</p>
                <p className="text-2xl font-semibold text-emerald-700">{rankingAudit.attributed_consultations || 0}</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-white p-4">
                <p className="text-sm font-medium text-amber-700">Unattributed</p>
                <p className="text-2xl font-semibold text-amber-700">{rankingAudit.unattributed_consultations || 0}</p>
              </div>
            </div>

            {rankingAudit.rows?.length > 0 ? (
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Pharmacist</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Included</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Assigned Today</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Completed Today</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Paid</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Hot Lead</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Cold Lead</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-12"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {rankingAudit.rows.map((row) => {
                    const isExpanded = expandedRankingRows[row.pharmacist_name]
                    return (
                      <React.Fragment key={row.pharmacist_name}>
                        <tr
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleRankingExpanded(row.pharmacist_name)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{row.rank}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{row.pharmacist_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-700">{row.total_cases}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-sky-700 font-medium">{row.assigned_today}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-blue-600 font-semibold">{row.completed_today}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-green-600 font-medium">{row.total_cases_paid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-orange-600 font-medium">{row.hot_lead}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-700 font-medium">{row.cold_lead}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-slate-400">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-slate-50">
                            <td colSpan={8} className="px-6 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Assigned + Completed Today</div>
                                  <div className="mt-1 text-lg font-semibold text-emerald-700">{row.same_day_completed}</div>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Completed Today From Earlier Assignment</div>
                                  <div className="mt-1 text-lg font-semibold text-amber-700">{row.completed_from_backlog}</div>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Assigned Today Only</div>
                                  <div className="mt-1 text-lg font-semibold text-sky-700">{row.assigned_today_only}</div>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Completed Total</div>
                                  <div className="mt-1 text-lg font-semibold text-slate-900">{row.completed}</div>
                                </div>
                              </div>

                              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                                <table className="min-w-full divide-y divide-slate-200">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">Consultation</th>
                                      <th className="px-4 py-3 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">User</th>
                                      <th className="px-4 py-3 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">Timing</th>
                                      <th className="px-4 py-3 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">Canonical</th>
                                      <th className="px-4 py-3 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider">Attributed Log</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {(row.evidence_rows || []).map((evidence) => (
                                      <tr key={evidence.consultation_id}>
                                        <td className="px-4 py-3 align-top">
                                          <div className="text-sm font-medium text-slate-900 font-mono">
                                            {evidence.consultation_id}
                                          </div>
                                          <div className="text-xs text-slate-500 mt-1">
                                            Assigned in consultation: {evidence.canonical_assigned_pharmacist || '-'}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          <div className="text-sm text-slate-900">{evidence.user_name || 'Unknown User'}</div>
                                          <div className="text-xs text-slate-500 mt-1">{evidence.user_phone || evidence.user_id || '-'}</div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getTimingBadge(evidence.timing_bucket).className}`}>
                                            {getTimingBadge(evidence.timing_bucket).label}
                                          </span>
                                          <div className="mt-2 text-xs text-slate-600">
                                            <div>Assigned business day: <span className="font-medium text-slate-900">{evidence.assigned_business_date || '-'}</span></div>
                                            <div className="mt-1">Completed business day: <span className="font-medium text-slate-900">{evidence.completed_business_date || '-'}</span></div>
                                            <div className="mt-1">Included by: <span className="font-medium text-slate-900">{evidence.inclusion_basis === 'completed_at' ? 'Completed At' : 'Assigned At Fallback'}</span></div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 align-top text-xs text-slate-600">
                                          <div>Status: <span className="font-medium text-slate-900">{formatLabel(evidence.consultation_status)}</span></div>
                                          <div className="mt-1">Outcome: <span className="font-medium text-slate-900">{formatLabel(evidence.consultation_outcome)}</span></div>
                                          <div className="mt-1">Completed: <span className="font-medium text-slate-900">{formatDateTime(evidence.canonical_completed_at)}</span></div>
                                        </td>
                                        <td className="px-4 py-3 align-top text-xs text-slate-600">
                                          <div>Log ID: <span className="font-medium text-slate-900 font-mono">{evidence.attributed_log_id || '-'}</span></div>
                                          <div className="mt-1">Assigned: <span className="font-medium text-slate-900">{formatDateTime(evidence.attributed_assigned_at)}</span></div>
                                          <div className="mt-1">Handled: <span className="font-medium text-slate-900">{formatDateTime(evidence.attributed_handled_at)}</span></div>
                                          <div className="mt-1">Type: <span className="font-medium text-slate-900">{formatLabel(evidence.attributed_assignment_type)}</span></div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-12 text-center text-slate-500">
                No pharmacist ranking rows were generated for this business date.
              </div>
            )}
          </div>
        )}

        {/* Summary Cards - Main Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Total RR Assigned</p>
            <p className="text-2xl font-semibold text-indigo-600">{grandTotals.round_robin_assigned}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Total Assigned & Processed</p>
            <p className="text-2xl font-semibold text-blue-600">{grandTotals.assigned_processed}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Total Wondura Handled</p>
            <p className="text-2xl font-semibold text-purple-600">{grandTotals.wondura_handled}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Total Pending</p>
            <p className="text-2xl font-semibold text-orange-600">{grandTotals.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Total Paid</p>
            <p className="text-2xl font-semibold text-green-600">{grandTotals.paid}</p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
            <p className="text-red-600 flex-1">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        )}

        {/* Reports Table */}
        {!loading && reportData.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No report data found for the selected date range</p>
          </div>
        )}

        {!loading && reportData.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Round Robin Assigned
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned & Processed
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wondura Handled
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">

                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((day) => {
                  const hasStatusOrOutcomeActivity =
                    Object.values(day.totals.status_counts || {}).some((value) => Number(value || 0) > 0) ||
                    Object.values(day.totals.outcome_counts || {}).some((value) => Number(value || 0) > 0)
                  const isExpanded = expandedDates[day.date]
                  const hasActivity =
                    (day.totals.round_robin_assigned || 0) > 0 ||
                    day.totals.assigned_processed > 0 ||
                    day.totals.wondura_handled > 0 ||
                    day.totals.pending > 0 ||
                    day.totals.paid > 0 ||
                    hasStatusOrOutcomeActivity

                  return (
                    <React.Fragment key={day.date}>
                      {/* Daily totals row */}
                      <tr
                        className={`hover:bg-gray-50 ${hasActivity ? 'cursor-pointer' : ''}`}
                        onClick={() => hasActivity && toggleExpanded(day.date)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {formatDate(day.date)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${(day.totals.round_robin_assigned || 0) > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                            {day.totals.round_robin_assigned || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${day.totals.assigned_processed > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {day.totals.assigned_processed}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${day.totals.wondura_handled > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                            {day.totals.wondura_handled}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${day.totals.pending > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                            {day.totals.pending}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${day.totals.paid > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {day.totals.paid}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {hasActivity && (
                            <span className="text-gray-400">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded pharmacist breakdown */}
                      {isExpanded && (
                        <>
                          <tr className="bg-indigo-50">
                            <td className="px-6 py-3 pl-12" colSpan={7}>
                              <div className="text-xs text-indigo-900 flex flex-wrap gap-6">
                                <span>
                                  <span className="font-semibold">Day Status Totals:</span>{' '}
                                  {formatCountsSummary(day.totals.status_counts)}
                                </span>
                                <span>
                                  <span className="font-semibold">Day Outcome Totals:</span>{' '}
                                  {formatCountsSummary(day.totals.outcome_counts)}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {Object.entries(day.pharmacists)
                            .filter(([_, stats]) =>
                              stats.round_robin_assigned > 0 ||
                              stats.assigned_processed > 0 ||
                              stats.wondura_handled > 0 ||
                              stats.pending > 0 ||
                              stats.paid > 0 ||
                              Object.values(stats.status_counts || {}).some((value) => Number(value || 0) > 0) ||
                              Object.values(stats.outcome_counts || {}).some((value) => Number(value || 0) > 0)
                            )
                            .map(([username, stats]) => (
                              <React.Fragment key={`${day.date}-${username}`}>
                                {/* Main pharmacist row */}
                                <tr className="bg-gray-50">
                                  <td className="px-6 py-3 whitespace-nowrap pl-12">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-medium text-xs">
                                          {username.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <div className="ml-3">
                                        <span className="text-sm text-gray-700 font-medium">{username}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-3 whitespace-nowrap text-center">
                                    <span className="text-sm text-indigo-600 font-medium">{stats.round_robin_assigned || 0}</span>
                                  </td>
                                  <td className="px-6 py-3 whitespace-nowrap text-center">
                                    <span className="text-sm text-gray-600">{stats.assigned_processed}</span>
                                  </td>
                                  <td className="px-6 py-3 whitespace-nowrap text-center">
                                    <span className="text-sm font-semibold text-purple-600">{stats.wondura_handled}</span>
                                  </td>
                                  <td className="px-6 py-3 whitespace-nowrap text-center">
                                    <span className="text-sm text-gray-600">{stats.pending}</span>
                                  </td>
                                  <td className="px-6 py-3 whitespace-nowrap text-center">
                                    <span className="text-sm text-gray-600">{stats.paid}</span>
                                  </td>
                                  <td className="px-6 py-3"></td>
                                </tr>
                                <tr className="bg-gray-50">
                                  <td className="px-6 pb-4 pt-0 pl-20" colSpan={7}>
                                    <div className="text-xs text-gray-600 flex flex-wrap gap-6">
                                      <span>
                                        <span className="font-semibold text-gray-700">Status:</span>{' '}
                                        {formatCountsSummary(stats.status_counts)}
                                      </span>
                                      <span>
                                        <span className="font-semibold text-gray-700">Outcome:</span>{' '}
                                        {formatCountsSummary(stats.outcome_counts)}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, reportData.length)}</span> of{' '}
                    <span className="font-medium">{reportData.length}</span> days
                  </p>
                </div>
                <div>
                  {totalPages > 1 && (
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={goToPrevPage}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                      </button>

                      {/* Simple pagination logic for now */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum
                        if (totalPages <= 5) {
                          pageNum = i + 1
                        } else if (currentPage <= 3) {
                          pageNum = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i
                        } else {
                          pageNum = currentPage - 2 + i
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                              ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum}
                          </button>
                        )
                      })}

                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  )
}

export default DailyConsultationReports
